import React, { useState } from 'react';
import {
  View,
  StyleSheet,
  Platform,
  ScrollView,
  Dimensions,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { palette } from '../../utils/palette';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Ionicons } from '@expo/vector-icons';
import Text from '../../components/common/Text';
import TextField from '../../components/common/TextField';
import Button from '../../components/common/Button';
import ResponsiveContainer from '../../components/shared/ResponsiveContainer';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../hooks/useAuth';
import { RootStackParamList } from '../../types/navigation';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';

const resendSchema = z.object({
  email: z.string().email('Enter a valid email address'),
});

type ResendForm = z.infer<typeof resendSchema>;

// Font sizes match Login/Register screens
const AUTH_FONTS = {
  titleSize: Platform.OS === 'web' ? 16 : 24,
  subtitleSize: Platform.OS === 'web' ? 17 : 14,
  bodySize: Platform.OS === 'web' ? 14 : 14,
};

// Design tokens: proportionate scale
const SCALE = {
  mobile: {
    space: { xs: 4, sm: 8, md: 12, lg: 16, xl: 20, xxl: 24 },
    font: {
      xs: 11,
      sm: AUTH_FONTS.bodySize,
      md: AUTH_FONTS.subtitleSize,
      lg: AUTH_FONTS.subtitleSize,
      xl: 20,
      xxl: AUTH_FONTS.titleSize,
    },
    icon: 48,
    iconCircle: 64,
    stepNum: 20,
    cardPadH: 20,
    cardPadV: 20,
    radius: 16,
  },
  tablet: {
    space: { xs: 5, sm: 8, md: 10, lg: 12, xl: 16, xxl: 20 },
    font: {
      xs: 12,
      sm: AUTH_FONTS.bodySize,
      md: AUTH_FONTS.subtitleSize,
      lg: AUTH_FONTS.subtitleSize,
      xl: 20,
      xxl: AUTH_FONTS.titleSize,
    },
    icon: 48,
    iconCircle: 64,
    stepNum: 20,
    cardPadH: 28,
    cardPadV: 18,
    radius: 16,
  },
  desktop: {
    space: { xs: 6, sm: 8, md: 10, lg: 12, xl: 16, xxl: 20 },
    font: {
      xs: 12,
      sm: AUTH_FONTS.bodySize,
      md: AUTH_FONTS.subtitleSize,
      lg: AUTH_FONTS.subtitleSize,
      xl: 20,
      xxl: AUTH_FONTS.titleSize,
    },
    icon: 48,
    iconCircle: 64,
    stepNum: 20,
    cardPadH: 36,
    cardPadV: 20,
    radius: 16,
  },
} as const;

const VerifyEmailRequiredScreen = () => {
  const { colors } = useTheme();
  const { user, resendVerification, signOut } = useAuth();
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { isMobile, isTablet } = useResponsiveLayout();
  const [resendSuccess, setResendSuccess] = useState(false);
  const defaultEmail = user?.email ?? '';

  const layout = isMobile
    ? SCALE.mobile
    : isTablet
      ? SCALE.tablet
      : SCALE.desktop;
  const s = layout.space;
  const formPadding = layout.cardPadH;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ResendForm>({
    resolver: zodResolver(resendSchema),
    defaultValues: { email: defaultEmail },
  });

  const onResend = async (data: ResendForm) => {
    setResendSuccess(false);
    try {
      await resendVerification(data.email);
      setResendSuccess(true);
    } catch {
      // Error handled by useAuth
    }
  };

  const isWeb = Platform.OS === 'web';

  const Content = (
    <View
      style={[
        styles.contentWrapper,
        isMobile && styles.mobileContentWrapper,
        isWeb && styles.webContentWrapper,
      ]}
    >
      <View
        style={[
          styles.formContainer,
          {
            backgroundColor: colors.card,
            shadowColor: colors.border,
            borderColor: colors.borderLight,
            paddingHorizontal: formPadding,
            paddingVertical: layout.cardPadV,
            borderRadius: layout.radius,
          },
        ]}
      >
        <View
          style={[
            styles.iconWrapper,
            {
              width: layout.iconCircle,
              height: layout.iconCircle,
              borderRadius: layout.iconCircle / 2,
              marginBottom: s.lg,
            },
          ]}
        >
          <Ionicons
            name="mail-open-outline"
            size={layout.icon}
            color={colors.primary}
          />
        </View>

        <Text
          style={[
            styles.formTitle,
            {
              color: colors.text,
              fontSize: AUTH_FONTS.titleSize,
              marginBottom: s.sm,
            },
          ]}
        >
          Check your email
        </Text>
        <Text
          style={[
            styles.formSubtitle,
            {
              color: colors.secondary,
              fontSize: AUTH_FONTS.subtitleSize,
              marginBottom: s.lg,
              paddingHorizontal: s.sm,
            },
          ]}
        >
          {isMobile
            ? 'Tap the link we sent to verify and sign in.'
            : isTablet
              ? 'Tap the link in your inbox to verify and sign in.'
              : "We've sent a verification link. Tap it to activate your account."}
        </Text>
        {defaultEmail ? (
          <View
            style={[
              styles.emailChip,
              {
                backgroundColor: colors.background,
                borderColor: colors.borderLight,
                paddingVertical: s.sm,
                paddingHorizontal: s.md,
                marginBottom: s.lg,
              },
            ]}
          >
            <Ionicons
              name="mail"
              size={layout.font.sm}
              color={colors.secondary}
              style={{ marginRight: s.sm }}
            />
            <Text
              weight="medium"
              numberOfLines={1}
              ellipsizeMode="tail"
              style={[
                styles.bodyText,
                {
                  color: colors.text,
                  flex: 1,
                  fontSize: AUTH_FONTS.bodySize,
                },
              ]}
            >
              {defaultEmail}
            </Text>
          </View>
        ) : null}

        <View style={[styles.steps, { gap: s.md, marginBottom: s.lg }]}>
          <View style={[styles.stepRow, { gap: s.md }]}>
            <View
              style={[
                styles.stepNumber,
                {
                  width: layout.stepNum,
                  height: layout.stepNum,
                  borderRadius: layout.stepNum / 2,
                  backgroundColor: colors.primary,
                  borderColor: colors.primary,
                },
              ]}
            >
              <Text
                weight="bold"
                style={{ color: palette.white, fontSize: layout.font.xs }}
              >
                1
              </Text>
            </View>
            <Text
              numberOfLines={2}
              style={[
                styles.stepText,
                styles.bodyText,
                { color: colors.text, fontSize: AUTH_FONTS.bodySize },
              ]}
            >
              Open your email app
            </Text>
          </View>
          <View style={[styles.stepRow, { gap: s.md }]}>
            <View
              style={[
                styles.stepNumber,
                {
                  width: layout.stepNum,
                  height: layout.stepNum,
                  borderRadius: layout.stepNum / 2,
                  backgroundColor: colors.primary,
                  borderColor: colors.primary,
                },
              ]}
            >
              <Text
                weight="bold"
                style={{ color: palette.white, fontSize: layout.font.xs }}
              >
                2
              </Text>
            </View>
            <Text
              numberOfLines={3}
              style={[
                styles.stepText,
                styles.bodyText,
                { color: colors.text, fontSize: AUTH_FONTS.bodySize },
              ]}
            >
              Tap &quot;Verify my email&quot; in the message from ColourSense
            </Text>
          </View>
          <View style={[styles.stepRow, { gap: s.md }]}>
            <View
              style={[
                styles.stepNumber,
                {
                  width: layout.stepNum,
                  height: layout.stepNum,
                  borderRadius: layout.stepNum / 2,
                  backgroundColor: colors.primary,
                  borderColor: colors.primary,
                },
              ]}
            >
              <Text
                weight="bold"
                style={{ color: palette.white, fontSize: layout.font.xs }}
              >
                3
              </Text>
            </View>
            <Text
              numberOfLines={2}
              style={[
                styles.stepText,
                styles.bodyText,
                { color: colors.text, fontSize: AUTH_FONTS.bodySize },
              ]}
            >
              You&apos;ll be taken to sign in
            </Text>
          </View>
        </View>

        <View style={[styles.actions, { marginBottom: s.lg }]}>
          <Text
            style={[
              styles.bodyText,
              {
                color: colors.text,
                fontSize: AUTH_FONTS.bodySize,
                fontWeight: '600',
                marginBottom: s.md,
              },
            ]}
          >
            {isMobile ? 'No email yet?' : "Didn't receive the email?"}
          </Text>
          {defaultEmail ? (
            <>
              <View
                style={[styles.buttonRow, isMobile && styles.buttonRowMobile]}
              >
                <Button
                  title="Resend verification email"
                  adjustsFontSizeToFit
                  onPress={() => {
                    setResendSuccess(false);
                    resendVerification(defaultEmail)
                      .then(() => setResendSuccess(true))
                      .catch(() => {});
                  }}
                  variant="outline"
                  style={[
                    styles.buttonRowItem,
                    isMobile && styles.buttonRowItemMobile,
                    isWeb && !isMobile && styles.buttonRowItemWeb,
                  ]}
                />
                <Button
                  title="Back to sign in"
                  variant="outline"
                  adjustsFontSizeToFit
                  onPress={async () => {
                    await signOut();
                    navigation.navigate('Login');
                  }}
                  style={[
                    styles.buttonRowItem,
                    isMobile && styles.buttonRowItemMobile,
                    isWeb && !isMobile && styles.buttonRowItemWeb,
                  ]}
                />
              </View>
              {resendSuccess && (
                <Text
                  variant="success"
                  style={[
                    styles.successText,
                    {
                      fontSize: AUTH_FONTS.bodySize,
                      marginTop: s.sm,
                    },
                  ]}
                >
                  New email sent. Check your inbox.
                </Text>
              )}
            </>
          ) : (
            <View style={[styles.resendForm, { gap: s.sm }]}>
              <Controller
                control={control}
                name="email"
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextField
                    label={isMobile ? 'Email' : 'Your email'}
                    placeholder={
                      isMobile
                        ? 'Your sign-up email'
                        : 'Enter the email you signed up with'
                    }
                    keyboardType="email-address"
                    autoCapitalize="none"
                    value={value}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    error={errors.email?.message}
                  />
                )}
              />
              <View
                style={[styles.buttonRow, isMobile && styles.buttonRowMobile]}
              >
                <Button
                  title="Send verification email"
                  adjustsFontSizeToFit
                  onPress={handleSubmit(onResend)}
                  style={[
                    styles.buttonRowItem,
                    isMobile && styles.buttonRowItemMobile,
                    isWeb && !isMobile && styles.buttonRowItemWeb,
                  ]}
                />
                <Button
                  title="Back to sign in"
                  variant="outline"
                  adjustsFontSizeToFit
                  onPress={async () => {
                    await signOut();
                    navigation.navigate('Login');
                  }}
                  style={[
                    styles.buttonRowItem,
                    isMobile && styles.buttonRowItemMobile,
                    isWeb && !isMobile && styles.buttonRowItemWeb,
                  ]}
                />
              </View>
              {resendSuccess && (
                <Text
                  variant="success"
                  style={[
                    styles.successText,
                    {
                      fontSize: AUTH_FONTS.bodySize,
                      marginTop: s.sm,
                    },
                  ]}
                >
                  Email sent. Check your inbox.
                </Text>
              )}
            </View>
          )}
        </View>
      </View>
    </View>
  );

  const Wrapper = isWeb ? (
    <View style={styles.webOuter}>
      <View style={styles.webInner}>{Content}</View>
    </View>
  ) : (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={[
        styles.scrollContent,
        isMobile && styles.scrollContentMobile,
      ]}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
    >
      {Content}
    </ScrollView>
  );

  return (
    <ResponsiveContainer
      style={[styles.container, { backgroundColor: colors.authBackground }]}
    >
      {Wrapper}
    </ResponsiveContainer>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  webOuter: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 24,
    paddingHorizontal: 20,
  },
  webInner: {
    width: '100%',
    maxWidth: 640,
  },
  scrollView: { flex: 1 },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 24,
    paddingHorizontal: 16,
    minHeight: Dimensions.get('window').height,
  },
  scrollContentMobile: {
    paddingVertical: 16,
    paddingHorizontal: 16,
    minHeight: undefined,
  },
  contentWrapper: {
    width: '100%',
    maxWidth: Platform.OS === 'web' ? 640 : '100%',
    alignSelf: 'center',
  },
  mobileContentWrapper: {
    paddingHorizontal: 8,
  },
  webContentWrapper: {
    maxWidth: 640,
  },
  formContainer: {
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 16,
    elevation: 8,
    borderWidth: 1,
    width: '100%',
    alignItems: 'center',
  },
  iconWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  formTitle: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    fontWeight: '800',
    letterSpacing: Platform.OS === 'web' ? -0.8 : -0.5,
    textShadowColor: 'rgba(0, 0, 0, 0.1)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
    textAlign: 'center',
    width: '100%',
  },
  formSubtitle: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
    opacity: 0.8,
    fontWeight: '500',
    letterSpacing: Platform.OS === 'web' ? 0.2 : 0.1,
    textAlign: 'center',
    width: '100%',
  },
  bodyText: {
    fontFamily:
      Platform.OS === 'web'
        ? 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        : 'System',
  },
  emailChip: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1,
    width: '100%',
  },
  steps: {
    width: '100%',
  },
  stepRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepNumber: {
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
  },
  stepText: {
    flex: 1,
  },
  actions: {
    width: '100%',
    alignItems: 'center',
  },
  resendLabel: {},
  resendForm: {
    width: '100%',
  },
  buttonRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 8,
  },
  buttonRowMobile: {
    flexDirection: 'column',
    gap: 6,
  },
  buttonRowItem: {
    flex: 1,
    minWidth: 200,
  },
  buttonRowItemWeb: {
    minWidth: 260,
  },
  buttonRowItemMobile: {
    width: '100%',
    flex: 0,
    flexBasis: 'auto',
    minWidth: 0,
  },
  successText: {
    textAlign: 'center',
  },
});

export default VerifyEmailRequiredScreen;
